import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Tooltip from "features/Account/modules/Tooltips/Tootips";
import { useIsDesktop } from "features/Common/modules/Viewport/utils/Viewport.utils";
import { getCustomizationAssets } from "features/ManageWebpage/modules/Customizations/common/utils/Customizations.utils";
import { useCustomizationStore } from "features/ManageWebpage/modules/Customizations/store/useCustomizationStore";
import { useWebpageSectionChange } from "features/ManageWebpage/modules/Customizations/store/useWebpageSectionChange";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import { getListingUuidFromUrlParam } from "ui/pages/ThankYou/utils";
import { UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL } from "features/AdminPanel/constants/AdminPanel.api";
import {
  SECTION_TYPES,
  CUSTOM_IMAGE_HANDLER_TEXTS,
} from "./CustomImageHandlerLayout.constants";
import PhotoWidgetV2 from "ui/widgets/photoUploader/PhotoWidgetV2";

export const CustomImageHandlerLayout = ({ sectionKey, sectionName }) => {
  const isDesktop = useIsDesktop();
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const sectionConfig = useCustomizationStore((state) => state.sectionConfig);
  const offeringSectionConfig = useAdminPanelStore(
    (state) => state.sectionConfig
  );
  const setIsSeeReferenceModalOpen = useAdminPanelStore(
    (state) => state.setIsSeeReferenceModalOpen
  );

  const { listing_uuid } = useParams();
  const listingId = getListingUuidFromUrlParam() || listing_uuid;

  const { handleSectionChange } = useWebpageSectionChange();
  const { handleSectionChange: handleOfferingsSectionChange } =
    useAccountSectionUnlocking();

  const [image, setImages] = useState(() => {
    const val =
      sectionConfig[sectionKey]?.value ||
      offeringSectionConfig[sectionKey]?.value ||
      [];
    return {
      [CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_1]: val[0] || null,
      [CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_2]: val[1] || null,
      [CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_3]: val[2] || null,
    };
  });

  const syncChanges = useCallback(
    (updatedImages) => {
      const values = Object.values(updatedImages);
      const payload = { feature_key: sectionKey, feature_value: values };

      if (sectionName === SECTION_TYPES.OFFERINGS) {
        payload.listing_uuid = listingId;
        handleOfferingsSectionChange({
          payload,
          endPoint: UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL,
        });
      } else if (sectionName === SECTION_TYPES.WEBPAGE) {
        handleSectionChange({ payload });
      }
    },
    [
      sectionKey,
      sectionName,
      listingId,
      handleOfferingsSectionChange,
      handleSectionChange,
    ]
  );

  const handleUpload = useCallback(
    (key) => (src) => {
      setImages((prev) => {
        if (prev[key] === src) return prev;
        const nextState = { ...prev, [key]: src };
        syncChanges(nextState);
        return nextState;
      });
    },
    [syncChanges]
  );

  const handleOpenModal = () => {
    setIsSeeReferenceModalOpen({
      id: CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKER_MODAL_ID,
      url: [
        CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKER_REF_URL_1,
        CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKER_REF_URL_2,
      ],
      height: CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKER_MODAL_HEIGHT,
      title: CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKERS_REF_TITLE,
      description: CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_MARKERS_REF_DESC,
    });
  };

  return (
    <div className="tw-px-4 md:tw-px-0">
      <div className="tw-flex md:tw-flex-col tw-flex-[3] md:tw-flex-[1]">
        <div className="tw-flex tw-items-center">
          <div className="tw-text-sm tw-font-medium tw-text-[#272522] tw-mr-1">
            {CUSTOM_IMAGE_HANDLER_TEXTS.ADD_TRUST_MARKERS_TITLE}
          </div>
          {!isDesktop && (
            <Tooltip
              id={CUSTOM_IMAGE_HANDLER_TEXTS.TOOLTIP_ID_IMAGES}
              open={
                openTooltipId === CUSTOM_IMAGE_HANDLER_TEXTS.TOOLTIP_ID_IMAGES
              }
              setOpenTooltipId={setOpenTooltipId}
              displayTitle={CUSTOM_IMAGE_HANDLER_TEXTS.TOOLTIP_DESC}
            />
          )}
        </div>
        {isDesktop && (
          <div className="tw-text-sm tw-font-normal">
            <span className="tw-text-[#272522] tw-opacity-60">
              {CUSTOM_IMAGE_HANDLER_TEXTS.DESCRIPTION_TEXT}
            </span>
            <span
              className="tw-text-[#493AB1] tw-cursor-pointer tw-whitespace-nowrap"
              onClick={handleOpenModal}
            >
              {CUSTOM_IMAGE_HANDLER_TEXTS.SEE_REFERENCE}
            </span>
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-[7px] tw-my-4">
        {[
          CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_1,
          CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_2,
          CUSTOM_IMAGE_HANDLER_TEXTS.TRUST_IMG_KEY_3,
        ].map((key) => (
          <PhotoWidgetV2
            key={key}
            buttonText={CUSTOM_IMAGE_HANDLER_TEXTS.HOVER_TEXT}
            selected={image[key]}
            width={150}
            height={33}
            onChange={handleUpload(key)}
            className=""
            scheduleType={null}
            aspect={150 / 33}
            disabled={false}
            defaultPlaceholderImg={getCustomizationAssets(
              CUSTOM_IMAGE_HANDLER_TEXTS.UPLOAD_MARKER_ICON
            )}
            query={CUSTOM_IMAGE_HANDLER_TEXTS.QUERY}
            uploadViaOptimization
            sectionName={sectionName}
            showButton={false}
            placeHolderClickable
            showRemoveButton
          />
        ))}
      </div>

      <div className="tw-text-[#818E9E] tw-text-sm tw-font-normal">
        {CUSTOM_IMAGE_HANDLER_TEXTS.RECOMMENDED_FORMAT}
      </div>
    </div>
  );
};
